import { defineStore } from 'pinia';
import { apiClient, ApiError } from '@/lib/api-client';

export interface AdminUser {
  readonly id: string;
  readonly displayName: string | null;
  readonly role: 'user' | 'host' | 'admin';
}

interface LoginResponse {
  readonly accessToken: string;
}

interface MeResponse {
  readonly user: AdminUser;
  readonly permissions: readonly string[];
}

interface AuthState {
  user: AdminUser | null;
  permissions: readonly string[];
  loading: boolean;
  initialised: boolean;
}

function fetchMe(): Promise<MeResponse> {
  return apiClient.request<MeResponse>('auth/me');
}

function postLogin(identifier: string, password: string): Promise<LoginResponse> {
  return apiClient.request<LoginResponse>('auth/login', {
    method: 'POST',
    body: { identifier, password },
    anonymous: true,
  });
}

async function postLogout(): Promise<void> {
  try {
    await apiClient.request('auth/logout', { method: 'POST' });
  } catch {

  }
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    permissions: [],
    loading: false,
    initialised: false,
  }),

  getters: {
    isAuthenticated: (state): boolean => state.user !== null,

    can:
      (state) =>
      (permission: string): boolean =>
        state.permissions.includes(permission),
  },

  actions: {
    apply(me: MeResponse | null): void {
      this.user = me?.user ?? null;
      this.permissions = me?.permissions ?? [];
    },

    async login(identifier: string, password: string): Promise<void> {
      this.loading = true;
      try {
        const result = await postLogin(identifier, password);
        apiClient.setAccessToken(result.accessToken);
        this.apply(await fetchMe());
      } finally {
        this.loading = false;
      }
    },

    async restore(): Promise<void> {
      if (this.initialised) {
        return;
      }
      try {
        this.apply(await fetchMe());
        this.initialised = true;
      } catch (error: unknown) {
        this.apply(null);
        this.initialised = error instanceof ApiError;
      }
    },

    async refresh(): Promise<void> {
      this.apply(await fetchMe());
    },

    async logout(): Promise<void> {
      await postLogout();
      apiClient.setAccessToken(null);
      this.apply(null);
    },
  },
});
